import React, { useState } from 'react';

import dynamic from 'next/dynamic';

import useSearchBar from '@components/views/hooks/useSearchBar';
import { LocationAnimationErrorBox } from '@components/views/components/listing/ListingStyle';
import {
  generateDateParam,
  getCurrentYearTab,
  getNextYearTab,
} from '@components/ui/search/SearchMonthDate/helpers';
import { YearTabProps } from '@components/ui/search/SearchMonthDate/interface';
import { FlexibleDiv } from '@components/formStyles';
import { useDevice } from '@contexts/device';
import Typography from '@components/Typography';

import {
  Content,
  Footer,
  StyledButton,
  StyledSimpleButton,
  SearchItemWrapper,
} from './styled';

const Modal = dynamic(() => import('@components/Modal/next/Modal'));
const DestinationSearch = dynamic(
  () =>
    import('@components/views/components/DestinationSearch/DestinationSearch')
);
const SearchMonthDate = dynamic(
  () => import('@components/ui/search/SearchMonthDate')
);
const MobileSearchBar = dynamic(
  () => import('@components/views/tour/components/SearchBar/MobileSearchBar')
);

interface TourSearchFormProps {
  onCancel?(): void;
  onSubmit?(): void;
  setShowLoader?: (show: boolean) => void;
}

const TourSearchForm = ({ onCancel, setShowLoader }: TourSearchFormProps) => {
  const { isDesktop } = useDevice();

  const [currentYearTab, setCurrentYearTab] = useState<YearTabProps>({
    year: getCurrentYearTab().year,
    months: [...getCurrentYearTab().months],
  });
  const [nextYearTab, setNextYearTab] = useState<YearTabProps>({
    year: getNextYearTab().year,
    months: [...getNextYearTab().months],
  });
  const {
    errors,
    destination,
    destinationSearchText,
    onDestinationTextChange,
    onDestinationChange,
  } = useSearchBar('tour');
  const [destinationError, setDestinationError] = useState(false);
  const onSearch = () => {
    if (destination === undefined) {
      setDestinationError(true);
    }
    if (destination?.redirectUrl) {
      window.open(`/${destination.url}`, '_self');
    } else if (destination && destination.url) {
      const dates = generateDateParam(currentYearTab, nextYearTab);
      const datesURL =
        Array.isArray(dates) && dates.length > 1 ? dates.join(',') : dates;
      const url = `${destination.url}${
        dates?.length ? `?date=${datesURL}` : ''
      }`;
      if ((destinationSearchText ?? '').length > 0) {
        setShowLoader?.(true);
        window.open(`/${url}`, '_self');
      }
    }
  };

  const handleOnMonthsDateUpdate = (info: YearTabProps[]) => {
    const [currentYear, nextYear] = info;

    setCurrentYearTab(currentYear);
    setNextYearTab(nextYear);
  };

  return isDesktop ? (
    <Modal
      title={
        <Typography variant='Title4' weight='SemiBold' color='D400'>
          Tur Ekleyin
        </Typography>
      }
      backgroundColor='lynxWhite'
      onCancel={onCancel}
      width={774}
      padding='44px'
      zIndex={98}
      borderRadius={8}
      reservationTour={true}
    >
      <Content>
        <FlexibleDiv bottom={10}>
          <LocationAnimationErrorBox
            borderRadius='8px'
            hasError={destinationError || !!errors.destination}
          >
            <SearchItemWrapper>
              <DestinationSearch
                placeholder='Yurt dışı turlar, Yurt içi turlar'
                onDestinationChange={onDestinationChange}
                searchText={destinationSearchText}
                onSearchTextChange={onDestinationTextChange}
                type='tour'
              />
            </SearchItemWrapper>
          </LocationAnimationErrorBox>
        </FlexibleDiv>
        <SearchItemWrapper>
          <SearchMonthDate
            onMonthsUpdated={handleOnMonthsDateUpdate}
            yearMonthsItemsStyle={{
              justifyContent: 'flex-start',
              gap: '14.7px',
            }}
          />
        </SearchItemWrapper>
      </Content>
      <Footer>
        <StyledSimpleButton onClick={onCancel}>Vazgeç</StyledSimpleButton>
        <StyledButton type='submit' onClick={onSearch} variant='Primary'>
          Turları Listele
        </StyledButton>
      </Footer>
    </Modal>
  ) : (
    <MobileSearchBar
      setIsRedirecting={setShowLoader}
      destination={destination}
      onDestinationChange={onDestinationChange}
      tourListCount={0}
      type='tour'
      defaultIsModalVisible={true}
      isSearchResultHeaderVisible={false}
      searchButtonTitle='Turları Listele'
      title={
        <Typography variant='Title4' weight='SemiBold' color='D400'>
          Tur Ekleyin
        </Typography>
      }
      onCancel={onCancel}
    />
  );
};

export default TourSearchForm;
